import { SEO } from './SEO'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  Globe,
  HelpCircle,
  ListChecks,
  Menu,
  MessageSquare,
  Network,
  Puzzle,
  Rocket,
  Search,
  Settings,
  Shield,
  Users,
  X
} from 'lucide-react'

const itemIconMap = {
  platformOverview: Rocket,
  gettingStarted: Rocket,
  conversationTasks: MessageSquare,
  projectsKnowledge: Globe,
  skillsNodes: Puzzle,
  collaborationControl: Shield,
  webProjects: Globe,
  creativeProduction: Settings,
  remoteOps: Network,
  desktopExecution: Settings,
  mobileNodes: MessageSquare,
  teamWorkflow: Users,
  suitableTasks: ListChecks,
  whatIsNode: Network,
  projectKnowledgeDiff: HelpCircle,
  whyNotChat: HelpCircle,
  capabilityBoundaries: Shield,
  openOverview: Rocket,
  serverConnection: Network,
  skillRegistration: Puzzle,
  protocolReference: Shield
}

const categoryIconMap = {
  features: Settings,
  scenarios: Rocket,
  advanced: Shield,
  faq: HelpCircle,
  gettingStarted: Rocket,
  skillDevelopment: Puzzle,
  protocolSpecs: Shield
}

const calloutToneClass = {
  info: 'border-primary/20 bg-primary/[0.08]',
  warning: 'border-secondary/30 bg-secondary/10'
}

const MarkdownBlock = ({ children }) => (
  <div className="docs-markdown mb-5">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
  </div>
)

const stripMarkdown = (value = '') =>
  value
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[.*?\]\(.*?\)/g, ' ')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/[*_>#|~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const flattenSectionText = (section) => {
  const values = []

  if (section.subtitle) values.push(section.subtitle)
  if (section.content) values.push(section.content)
  if (section.markdown) values.push(stripMarkdown(section.markdown))
  if (section.bullets) values.push(...section.bullets)
  if (section.steps) values.push(...section.steps)
  if (section.cards) {
    section.cards.forEach((card) => values.push(card.title, card.desc))
  }
  if (section.links) {
    section.links.forEach((link) => values.push(link.label, link.desc, link.href))
  }
  if (section.callout) {
    values.push(section.callout.title, section.callout.content)
  }

  return values.filter(Boolean).join(' ')
}

const getItemIcon = (key) => itemIconMap[key] || ChevronRight
const getCategoryIcon = (key) => categoryIconMap[key] || HelpCircle

const fallbackCopyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

const serializeSection = (section, index) => {
  const lines = []
  const title = section.subtitle || `Section ${index + 1}`

  lines.push(`## ${title}`)

  if (section.content) {
    lines.push(section.content)
  }

  if (section.markdown) {
    lines.push(section.markdown.trim())
  }

  if (section.callout) {
    lines.push(`> ${section.callout.title}`)
    lines.push(section.callout.content)
  }

  if (section.bullets) {
    section.bullets.forEach((bullet) => lines.push(`- ${bullet}`))
  }

  if (section.steps) {
    section.steps.forEach((step, stepIndex) => lines.push(`${stepIndex + 1}. ${step}`))
  }

  if (section.cards) {
    section.cards.forEach((card) => {
      lines.push(`### ${card.title}`)
      lines.push(card.desc)
    })
  }

  if (section.links) {
    section.links.forEach((link) => {
      lines.push(`- ${link.label}: ${link.href}`)
      if (link.desc) {
        lines.push(`  ${link.desc}`)
      }
    })
  }

  return lines.join('\n').trim()
}

const buildDocText = (docsData, currentContent) => {
  if (!currentContent) {
    return docsData.pageTitle || ''
  }

  const lines = [`# ${currentContent.title || docsData.pageTitle}`]

  if (currentContent.summary || docsData.pageSubtitle) {
    lines.push(currentContent.summary || docsData.pageSubtitle)
  }

  if (typeof window !== 'undefined') {
    lines.push(`${docsData.sourceLabel || 'Source'}: ${window.location.href}`)
  }

  ;(currentContent.sections || []).forEach((section, index) => {
    const serialized = serializeSection(section, index)
    if (serialized) {
      lines.push(serialized)
    }
  })

  return lines.join('\n\n').replace(/\n{3,}/g, '\n\n').trim()
}

const DocsCenter = ({ docsData, sectionToPath, basePath }) => {
  const { section } = useParams()
  const navigate = useNavigate()

  const defaultSection = docsData.defaultSection || Object.keys(sectionToPath)[0]
  const pathToSection = useMemo(
    () => Object.fromEntries(Object.entries(sectionToPath).map(([key, value]) => [value, key])),
    [sectionToPath]
  )
  const validSections = useMemo(() => Object.keys(sectionToPath), [sectionToPath])

  const [activeSection, setActiveSection] = useState(defaultSection)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [copyStatus, setCopyStatus] = useState('idle')

  useEffect(() => {
    if (section) {
      const mappedSection = pathToSection[section]
      if (mappedSection && validSections.includes(mappedSection)) {
        setActiveSection(mappedSection)
      } else {
        navigate(`${basePath}/${sectionToPath[defaultSection]}/`, { replace: true })
      }
      return
    }

    setActiveSection(defaultSection)
  }, [section, pathToSection, validSections, navigate, basePath, sectionToPath, defaultSection])

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const searchResults = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase()
    if (!keyword) return []

    return docsData.categories
      .flatMap((category) =>
        category.items.map((item) => {
          const content = docsData.content[item.id]
          const fullText = [
            category.title,
            item.name,
            item.desc,
            content?.title,
            content?.summary,
            ...(content?.sections || []).map(flattenSectionText)
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()

          return {
            ...item,
            categoryTitle: category.title,
            matches: fullText.includes(keyword)
          }
        })
      )
      .filter((item) => item.matches)
  }, [docsData, searchQuery])

  const currentContent = docsData.content[activeSection]

  const handleSectionChange = (sectionKey) => {
    setActiveSection(sectionKey)
    setMobileMenuOpen(false)
    navigate(`${basePath}/${sectionToPath[sectionKey]}/`)
  }

  const handleCopyCurrentDoc = async () => {
    try {
      await fallbackCopyText(buildDocText(docsData, currentContent))
      setCopyStatus('success')
    } catch (error) {
      setCopyStatus('error')
    }

    window.setTimeout(() => setCopyStatus('idle'), 2000)
  }

  const renderSection = (sectionData, index) => (
    <section key={`${sectionData.subtitle}-${index}`} className="mb-10">
      <h2 className="mb-4 text-2xl font-semibold text-text-primary">{sectionData.subtitle}</h2>

      {sectionData.content && (
        <p className="mb-5 whitespace-pre-line text-text-secondary leading-8">{sectionData.content}</p>
      )}

      {sectionData.markdown && <MarkdownBlock>{sectionData.markdown}</MarkdownBlock>}

      {sectionData.callout && (
        <div
          className={`rounded-2xl border p-5 mb-5 ${calloutToneClass[sectionData.callout.tone || 'info']}`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 text-primary" size={18} />
            <div>
              <h3 className="mb-2 font-semibold text-text-primary">{sectionData.callout.title}</h3>
              <p className="leading-7 text-text-secondary">{sectionData.callout.content}</p>
            </div>
          </div>
        </div>
      )}

      {sectionData.bullets && (
        <ul className="space-y-3 mb-5">
          {sectionData.bullets.map((bullet, bulletIndex) => (
            <li key={bulletIndex} className="flex items-start gap-3 text-text-secondary">
              <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
              <span className="leading-7">{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {sectionData.steps && (
        <ol className="space-y-3 mb-5">
          {sectionData.steps.map((step, stepIndex) => (
            <li key={stepIndex} className="flex items-start gap-3">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary-dark to-primary text-sm font-bold text-white">
                {stepIndex + 1}
              </span>
              <span className="pt-0.5 leading-7 text-text-secondary">{step}</span>
            </li>
          ))}
        </ol>
      )}

      {sectionData.cards && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 mb-5">
          {sectionData.cards.map((card, cardIndex) => (
            <div key={cardIndex} className="glass-effect rounded-2xl border border-border p-5">
              <h3 className="mb-2 text-lg font-semibold text-text-primary">{card.title}</h3>
              <p className="leading-7 text-text-secondary">{card.desc}</p>
            </div>
          ))}
        </div>
      )}

      {sectionData.links && (
        <div className="space-y-3">
          {sectionData.links.map((link, linkIndex) => (
            <a
              key={linkIndex}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className="glass-effect block rounded-2xl border border-border p-4 transition-colors hover:border-primary/30 hover:bg-primary/5"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="mb-1 font-medium text-text-primary">{link.label}</div>
                  {link.desc && <div className="text-sm leading-6 text-text-secondary">{link.desc}</div>}
                </div>
                <ExternalLink className="flex-shrink-0 text-primary" size={18} />
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  )

  const copyButtonLabel =
    copyStatus === 'success'
      ? docsData.copySuccessLabel || '已复制'
      : copyStatus === 'error'
        ? docsData.copyErrorLabel || '复制失败'
        : docsData.copyButtonLabel || '复制当前文档'

  return (
    <>
      <SEO
        title={currentContent?.title || docsData.pageTitle}
        description={currentContent?.summary || docsData.pageSubtitle}
      />
      <div className="pt-24 pb-20 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setMobileMenuOpen((value) => !value)}
              className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-primary to-secondary text-white shadow-[0_18px_34px_rgba(255,95,135,0.24)] lg:hidden"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <AnimatePresence>
              {(mobileMenuOpen || isDesktop) && (
                <motion.aside
                  initial={{ x: -300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -300, opacity: 0 }}
                  className="fixed left-0 top-24 z-40 h-[calc(100vh-6rem)] w-72 overflow-y-auto rounded-2xl border border-border bg-white p-6 shadow-[0_20px_46px_rgba(27,35,51,0.10)] lg:sticky lg:z-0"
                >
                  <h2 className="mb-4 text-xl font-bold text-text-primary">{docsData.menuTitle}</h2>

                  <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={16} />
                    <input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder={docsData.searchPlaceholder}
                      className="w-full rounded-xl border border-border bg-void-lighter py-3 pl-10 pr-4 text-sm text-text-primary outline-none transition-colors placeholder:text-text-muted focus:border-primary/35 focus:bg-white"
                    />
                  </div>

                  {searchQuery.trim() ? (
                    <div className="space-y-2">
                      {searchResults.length > 0 ? (
                        searchResults.map((item) => {
                          const ItemIcon = getItemIcon(item.id)
                          return (
                            <button
                              key={item.id}
                              onClick={() => handleSectionChange(item.id)}
                              className="w-full rounded-xl border border-border bg-white px-4 py-3 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
                            >
                              <div className="flex items-start gap-3">
                                <ItemIcon size={16} className="mt-1 flex-shrink-0 text-primary" />
                                <div>
                                  <div className="text-sm font-medium text-text-primary">{item.name}</div>
                                  <div className="mt-1 text-xs text-text-muted">{item.categoryTitle}</div>
                                  <div className="mt-1 text-xs leading-5 text-text-secondary">{item.desc}</div>
                                </div>
                              </div>
                            </button>
                          )
                        })
                      ) : (
                        <div className="rounded-xl border border-border bg-void-lighter px-4 py-4 text-sm leading-6 text-text-secondary">
                          {docsData.searchEmpty}
                        </div>
                      )}
                    </div>
                  ) : (
                    <nav className="space-y-6">
                      {docsData.categories.map((category) => {
                        const CategoryIcon = getCategoryIcon(category.id)

                        return (
                          <div key={category.id}>
                            <div className="flex items-center space-x-2 mb-3">
                              <CategoryIcon size={18} className="text-primary" />
                              <h3 className="text-sm font-semibold uppercase tracking-wider text-text-muted">
                                {category.title}
                              </h3>
                            </div>
                            <ul className="ml-6 space-y-1 border-l border-border pl-4">
                              {category.items.map((item) => {
                                const ItemIcon = getItemIcon(item.id)
                                return (
                                  <li key={item.id}>
                                    <Link
                                      to={`${basePath}/${sectionToPath[item.id]}/`}
                                      onClick={() => setMobileMenuOpen(false)}
                                      className={`w-full text-left px-3 py-2 rounded-lg transition-all flex items-start space-x-2 ${
                                        activeSection === item.id
                                          ? 'bg-primary/10 text-primary'
                                          : 'text-text-secondary hover:bg-primary/5 hover:text-primary'
                                      }`}
                                    >
                                      <ItemIcon size={16} className="mt-0.5 flex-shrink-0" />
                                      <span className="text-sm leading-6">{item.name}</span>
                                    </Link>
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                        )
                      })}
                    </nav>
                  )}
                </motion.aside>
              )}
            </AnimatePresence>

            <main className="flex-1 min-w-0">
              <div className="glass-effect rounded-2xl p-8 md:p-12">
                <motion.div
                  key={activeSection}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mb-8 flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <div className="mb-3 text-sm uppercase tracking-[0.2em] text-primary">
                        {docsData.pageTitle}
                      </div>
                      <h1 className="mb-4 text-4xl font-bold text-text-primary md:text-5xl">
                        {currentContent?.title || docsData.pageTitle}
                      </h1>
                      <p className="text-lg leading-8 text-text-secondary">
                        {currentContent?.summary || docsData.pageSubtitle}
                      </p>
                    </div>

                    <div className="flex-shrink-0">
                      <button
                        onClick={handleCopyCurrentDoc}
                        className="inline-flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm font-medium text-primary-dark transition-colors hover:border-primary/35 hover:bg-primary/15"
                      >
                        {copyStatus === 'success' ? <Check size={16} /> : <Copy size={16} />}
                        <span>{copyButtonLabel}</span>
                      </button>
                      <p className="mt-3 max-w-xs text-xs leading-5 text-text-muted">
                        {docsData.copyButtonHint || '复制当前页面完整内容，方便直接交给 AI 继续编写或实现。'}
                      </p>
                    </div>
                  </div>

                  {currentContent?.sections?.map(renderSection) || (
                    <div className="py-20 text-center text-text-secondary">{docsData.comingSoon}</div>
                  )}

                  <div className="mt-12 rounded-2xl border border-border bg-void-lighter p-6">
                    <div className="flex items-start gap-3">
                      <Search className="mt-1 text-primary" size={18} />
                      <div>
                        <h3 className="mb-2 font-semibold text-text-primary">
                          {docsData.usageTipTitle || '使用建议'}
                        </h3>
                        <p className="leading-7 text-text-secondary">
                          {docsData.usageTipContent ||
                            '当你准备让 Avelia 执行真实任务时，优先说明目标、上下文、限制条件和交付形式。对复杂任务，优先按阶段组织能力与执行面，而不是把所有要求一次性堆给单个会话。'}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}

export default DocsCenter
